import { useEffect } from 'react';
import { useAuthtoken, logout } from 'stores/useAuthStore';
import { queryOptions, useQuery } from '@tanstack/react-query';
import type {
  GridFilterItem,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';

import request from 'utils/request';
import type { ApiRespBody } from 'types/api';


const TOKEN_INVALID = 'Token is not valid';

type ClockingRow = Record<string, unknown> & {
  id?: number;
  empID?: string | number;
  firstName?: string;
  lastName?: string;
};

type Employee = {
  empID?: string | number;
  firstName?: string;
  lastName?: string;
};

function employeeData(
  result: ApiRespBody<Employee[]> | null,
  fallbackMessage: string
): Employee[] {
  if (result?.success) return result.data ?? [];
  if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(result?.message || fallbackMessage);
}

export interface ClockingRecsParams {
  itemType: string;
  disablePagination: boolean;
  forceRefresh: boolean;
  globalSearch: string;
  paginationModel: GridPaginationModel;
  filterModel?: GridFilterModel;
  sortModel?: GridSortModel;
}

export interface ClockingRecsResult {
  rows: ClockingRow[];
  rowCount: number;
}

export const DEFAULT_CLOCKING_RECS_PARAMS: ClockingRecsParams = {
  itemType: 'clockingRecs',
  disablePagination: false,
  forceRefresh: false,
  globalSearch: '',
  paginationModel: { page: 0, pageSize: 25 },
  filterModel: { items: [] },
  sortModel: [],
};

function buildSortString(sortModel?: GridSortModel): string {
  if (!sortModel) return '';
  return sortModel
    .map((sort) => `${sort.field}:${sort.sort}`)
    .join(',');
}

function buildFilterString(filterModel?: GridFilterModel): string {
  if (!filterModel?.items?.length) return '';
  let filterString = '&';
  filterModel.items.forEach((filter: GridFilterItem, i: number) => {
    const { value } = filter;
    const operator =
      filter.operator === 'equals' || filter.operator === 'isEmpty'
        ? ''
        : filter.operator.charAt(0).toUpperCase() + filter.operator.slice(1);
    filterString += `${i > 0 ? '&' : ''}${filter.field}${operator}=${
      value ? encodeURIComponent(value) : ''
    }`;
  });
  return filterString;
}

export async function fetchClockingRecs(
  params: ClockingRecsParams,
  authToken: string
): Promise<ClockingRecsResult> {
  const {
    itemType,
    disablePagination,
    forceRefresh,
    globalSearch,
    paginationModel,
    filterModel,
    sortModel,
  } = params;

  const headers = {
    'Content-Type': 'application/json',
    authorization: authToken,
  };

  const sortString = buildSortString(sortModel);
  let filterString = buildFilterString(filterModel);

  if (globalSearch !== '') {
    const employeeMatch = employeeData(
      await request<ApiRespBody<Employee[]>>(
      `/api/employees?globalSearchFields=empID,firstName,lastName&globalSearchValue=${globalSearch}`,
      { method: 'GET', headers }
      ),
      'Failed to search employees'
    );

    if (employeeMatch.length === 0) return { rows: [], rowCount: 0 };
    filterString += `&empIDIsAnyOf=${employeeMatch
      .map((employee) => employee.empID)
      .join(',')}`;
  }

  const offsetLimitString = disablePagination
    ? ''
    : `&offset=${paginationModel.page * paginationModel.pageSize}&limit=${
        paginationModel.pageSize + 1
      }`;
  const ttlSecsString = forceRefresh ? '&ttlSecs=0' : '';

  const result = await request<ApiRespBody<ClockingRow[]>>(
    `/api/${itemType}?${offsetLimitString}${
      sortString !== '' ? `&orderBy=${sortString}` : ''
    }${filterString}${ttlSecsString}`,
    { method: 'GET', headers }
  );

  if (!result?.success || !result.data) {
    if (result?.message === TOKEN_INVALID) {
      throw new Error(TOKEN_INVALID);
    }
    throw new Error(result?.message || 'Failed to load clocking records');
  }

  const rows = result.data;
  rows.forEach((row, index) => {
    row.id = index;
  });

  const rowCount =
    rows.length > paginationModel.pageSize
      ? -1
      : rows.length + paginationModel.page * paginationModel.pageSize;

  if (rowCount === -1 && !disablePagination) {
    rows.pop();
  }

  const activeEmployees = employeeData(
    await request<ApiRespBody<Employee[]>>(`/api/employees?active=true`, {
      method: 'GET',
      headers,
    }),
    'Failed to load active employees'
  );

  if (activeEmployees?.length) {
    rows.forEach((row) => {
      const employee = activeEmployees.find((emp) => emp.empID === row.empID);
      if (employee) {
        row.firstName = employee.firstName;
        row.lastName = employee.lastName;
      }
    });
  }

  return { rows, rowCount };
}

export function clockingRecsQueryOptions(
  params: ClockingRecsParams,
  authToken: string
) {
  return queryOptions({
    queryKey: ['clockingRecs', params],
    queryFn: () => fetchClockingRecs(params, authToken),
  });
}

export function useClockingRecsQuery(params: ClockingRecsParams) {
  const authToken = useAuthtoken();
  const query = useQuery(clockingRecsQueryOptions(params, authToken));

  useEffect(() => {
    if (query.error instanceof Error && query.error.message === TOKEN_INVALID) {
      logout();
    }
  }, [query.error]);

  return query;
}
